import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DollarSign, Plus, Search, Edit, Trash2, Eye, X,
  Percent, TrendingUp, TrendingDown, Calculator
} from 'lucide-react';

interface Tax {
  id: number;
  nameEN: string;
  nameAR: string;
  percentage: number;
  isActive: boolean;
}

export default function TaxesPage() {
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingTax, setEditingTax] = useState<Tax | null>(null);
  const [selectedTax, setSelectedTax] = useState<Tax | null>(null);
  const [form, setForm] = useState({
    nameEN: '', nameAR: '', percentage: '', isActive: true
  });

  useEffect(() => { loadTaxes(); }, []);

  const loadTaxes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/taxes');
      let data = [];
      if (response.data?.success) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
      setTaxes(data);
    } catch (error) {
      console.error('Error loading taxes:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTax(null);
    setForm({ nameEN: '', nameAR: '', percentage: '', isActive: true });
    setShowModal(true);
  };

  const openEditModal = (tax: Tax) => {
    setEditingTax(tax);
    setForm({
      nameEN: tax.nameEN || '',
      nameAR: tax.nameAR || '',
      percentage: tax.percentage.toString(),
      isActive: tax.isActive !== false
    });
    setShowModal(true);
  };

  const openViewModal = (tax: Tax) => {
    setSelectedTax(tax);
    setShowViewModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        nameEN: form.nameEN,
        nameAR: form.nameAR,
        percentage: parseFloat(form.percentage) || 0,
        isActive: form.isActive
      };

      if (editingTax) {
        await api.put('/api/taxes/' + editingTax.id, payload);
        alert("تم تحديث الضريبة بنجاح");
      } else {
        await api.post('/api/taxes', payload);
        alert("تم إضافة الضريبة بنجاح");
      }
      setShowModal(false);
      loadTaxes();
    } catch (error: any) {
      alert(error.response?.data?.message || "حدث خطأ في الحفظ");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه الضريبة؟")) return;
    try {
      await api.delete('/api/taxes/' + id);
      loadTaxes();
      alert("تم حذف الضريبة بنجاح");
    } catch (error: any) {
      alert(error.response?.data?.message || "حدث خطأ في الحذف");
    }
  };

  const filteredTaxes = Array.isArray(taxes) ? taxes.filter(t =>
    (t.nameEN || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.nameAR || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: taxes.length,
    active: taxes.filter(t => t.isActive !== false).length,
    inactive: taxes.filter(t => t.isActive === false).length,
    avgPercentage: taxes.length > 0 ? (taxes.reduce((sum, t) => sum + (t.percentage || 0), 0) / taxes.length).toFixed(1) : 0
  };

  const inputClass = "w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition";

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <span>الضرائب</span>
              <p className="text-sm text-gray-500 font-normal mt-1">إدارة الضرائب والرسوم</p>
            </div>
          </h1>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg"
        >
          <Plus className="w-5 h-5 ml-2" />
          <span>إضافة ضريبة جديدة</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">إجمالي الضرائب</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">الضرائب النشطة</p>
            <p className="text-2xl font-bold text-green-700">{stats.active}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-gray-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">الضرائب غير النشطة</p>
            <p className="text-2xl font-bold text-gray-700">{stats.inactive}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg hover:shadow-xl transition">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calculator className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">متوسط النسبة</p>
            <p className="text-2xl font-bold text-blue-700">{stats.avgPercentage}%</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="بحث باسم الضريبة..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">جاري تحميل الضرائب...</p>
            </div>
          ) : filteredTaxes.length === 0 ? (
            <div className="p-12 text-center">
              <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد ضرائب</h3>
              <p className="text-gray-500">ابدأ بإضافة ضريبة جديدة</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الرقم</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم الضريبة (عربي)</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">اسم الضريبة (إنجليزي)</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">النسبة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الحالة</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTaxes.map((tax) => (
                    <tr key={tax.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <span className="font-mono font-bold text-orange-600">{tax.id}</span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white">
                            <Percent className="w-5 h-5" />
                          </div>
                          <span className="font-semibold text-gray-900">{tax.nameAR || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className="text-gray-700">{tax.nameEN || '-'}</span>
                      </td>
                      <td className="py-4 px-5">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-bold">
                          <Percent className="w-3.5 h-3.5" />
                          {tax.percentage}%
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          tax.isActive !== false 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {tax.isActive !== false ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openViewModal(tax)}
                            className="p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition"
                            title="عرض"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(tax)}
                            className="p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition"
                            title="تعديل"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(tax.id)}
                            className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition"
                            title="حذف"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  {editingTax ? 'تعديل الضريبة' : 'إضافة ضريبة جديدة'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>اسم الضريبة (عربي) *</Label>
                    <Input
                      value={form.nameAR}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, nameAR: e.target.value })}
                      required
                      className={inputClass}
                      placeholder="مثال: ضريبة القيمة المضافة"
                    />
                  </div>
                  <div>
                    <Label>اسم الضريبة (إنجليزي) *</Label>
                    <Input
                      value={form.nameEN}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, nameEN: e.target.value })}
                      required
                      className={inputClass}
                      placeholder="Example: VAT"
                    />
                  </div>
                  <div>
                    <Label>النسبة (%) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.percentage}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, percentage: e.target.value })}
                      required
                      className={inputClass}
                      placeholder="14.00"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isActive" className="text-sm font-medium">ضريبة نشطة</label>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">إلغاء</Button>
                  <Button type="submit" className="flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
                    {editingTax ? 'تحديث' : 'إضافة'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedTax && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">تفاصيل الضريبة</h2>
                <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white">
                    <Percent className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedTax.nameAR}</h3>
                    <p className="text-sm text-gray-500">{selectedTax.nameEN}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">النسبة</p>
                    <p className="font-semibold text-blue-600 text-2xl">{selectedTax.percentage}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">الحالة</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                      selectedTax.isActive !== false 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {selectedTax.isActive !== false ? 'نشط' : 'غير نشط'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedTax); }} className="flex-1 bg-orange-500 hover:bg-orange-600">
                    <Edit className="w-4 h-4 ml-2" />
                    تعديل الضريبة
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
